import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import * as NavConstants from 'constants/NavConstants';
import FileStates from 'FileStates';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatDraftStatus, createChat, setDraftMessage} from 'llm/chat-library/ChatActions';
import {CreateChatOptions} from 'llm/chat-library/ChatDAO';
import {openExistingChat} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {getChatTypeForNewAgentChat} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';

/**
 * Creates a new chat for an entry and opens the AI sidepanel in semi-expanded mode.
 */
export interface CreateChatForEntryOptions {
  /**
   * Whether this chat is creating write context (target entry) or read context (workspace context).
   */
  assistantScope?: 'read' | 'write';
}

export default async function createChatForEntry(
  entryId: string,
  agentType: AgentType,
  {assistantScope = 'write'}: CreateChatForEntryOptions = {}
): Promise<void> {
  const selectedAgentType =
    BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT &&
    (agentType === AgentType.ASK ||
      agentType === AgentType.DATA_CAPTURE ||
      agentType === AgentType.DEEP_RESEARCH ||
      agentType === AgentType.ANALYSIS)
      ? AgentType.ASSISTANT
      : agentType;

  if (selectedAgentType === AgentType.ASSISTANT && assistantScope === 'read') {
    // For read-scope assistant chats, context is derived from the active workspace item.
    await FileStates.get().openFolderItem(entryId, {open: true});
  }

  const chatType = getChatTypeForNewAgentChat(selectedAgentType);
  const createOptions: CreateChatOptions = {};
  if (selectedAgentType === AgentType.DATA_CAPTURE) {
    createOptions[AgentType.DATA_CAPTURE] = {targetEntryId: entryId};
  } else if (selectedAgentType === AgentType.ANALYSIS) {
    createOptions[AgentType.ANALYSIS] = {targetEntryId: entryId};
  } else if (selectedAgentType === AgentType.ASSISTANT && assistantScope === 'write') {
    createOptions[AgentType.ASSISTANT] = {targetEntryId: entryId};
  }
  const {chatSessionId} = await createChat(chatType, createOptions, selectedAgentType);
  trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CREATED_CHAT_FOR_ENTRY, {
    agentType: selectedAgentType,
    entryId,
    chatSessionId,
  });
  setDraftMessage(chatSessionId, {
    status: ChatDraftStatus.DRAFT,
    message: {text: '', selectedFiles: []},
    settings: {agentType: selectedAgentType, publicDataEnabled: false},
  });
  openExistingChat(chatSessionId);
  SidepanelActions.openSection(NavConstants.SECTIONS.AI);
}
